import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export function BulkPspConsentDialog({ open, onClose, customerFilter }) {
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!open) return;
    setResult(null); setRows(null);
    api.get("/psp-consent/missing", { params: customerFilter && customerFilter !== "all" ? { customer_id: customerFilter } : {} })
      .then(({ data }) => { setRows(data); setPicked(Object.fromEntries(data.map((r) => [r.id, r.reachable && !r.pending]))); })
      .catch((err) => { toast.error(formatApiError(err)); setRows([]); });
  }, [open, customerFilter]);
  const ids = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/psp-consent/send-bulk", { driver_ids: ids }); setResult(data); toast.success(`PSP authorization sent to ${data.sent.length} driver${data.sent.length === 1 ? "" : "s"}`); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(!!result)}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-psp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-blue-700" />Send PSP authorization</DialogTitle>
          <DialogDescription>Every active driver without a signed PSP Disclosure &amp; Authorization. Each gets a text/email link; signed copies file in the DQ binder automatically.</DialogDescription>
        </DialogHeader>
        {rows === null && <div className="text-sm text-slate-500">Loading…</div>}
        {rows && rows.length === 0 && <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="bulk-psp-none">Every active driver already has a signed PSP authorization.</div>}
        {rows && rows.length > 0 && !result && (
          <>
            <div className="flex items-center gap-3 text-xs text-slate-600"><span data-testid="bulk-psp-count">{ids.length} of {rows.length} selected</span><button type="button" className="underline" onClick={() => setPicked(Object.fromEntries(rows.map((r) => [r.id, r.reachable])))}>All reachable</button><button type="button" className="underline" onClick={() => setPicked({})}>None</button></div>
            <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100 rounded-md border border-slate-200" data-testid="bulk-psp-list">
              {rows.map((r) => (
                <li key={r.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                  <Checkbox checked={!!picked[r.id]} disabled={!r.reachable} onCheckedChange={(v) => setPicked((p) => ({ ...p, [r.id]: !!v }))} data-testid={`bulk-psp-pick-${r.id}`} />
                  <span className="flex-1 truncate">{r.name}</span>
                  {r.pending && <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">Sent {(r.requested_at || "").slice(0, 10)} · awaiting</Badge>}
                  {!r.reachable && <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700" title="Add an email or mobile number to the driver first">No email / phone</Badge>}
                </li>
              ))}
            </ul>
          </>
        )}
        {result && (
          <div className="space-y-2 text-sm" data-testid="bulk-psp-result">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-emerald-800">Sent to {result.sent.length}: {result.sent.join(", ") || "—"}</div>
            {result.skipped.length > 0 && <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-800">Skipped {result.skipped.length}: {result.skipped.map((s) => `${s.driver} (${s.reason})`).join("; ")}</div>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(!!result)} data-testid="bulk-psp-close">{result ? "Done" : "Cancel"}</Button>
          {!result && rows?.length > 0 && <Button disabled={busy || ids.length === 0} onClick={send} className="bg-blue-700 hover:bg-blue-800" data-testid="bulk-psp-send">Send to {ids.length} driver{ids.length === 1 ? "" : "s"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
